import { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion } from 'framer-motion';
import { FaInstagram, FaEnvelope } from 'react-icons/fa';
import { GiPerspectiveDiceSixFacesRandom, GiBookCover, GiDragonHead, GiScrollQuill } from 'react-icons/gi';
import logo from '../assets/logo.png';
import capLogo from '../assets/cap-logo.webp';
import itzalScales from '../assets/itzal-scales.webp';

// ─── Paletas ──────────────────────────────────────────────────────────────────
// CAP (esquerda, cyberpunk) e ITZAL (direita, medieval dourado). Cores extraidas
// da identidade visual de cada jogo.

const CAP = {
  bg: '#0b0b1c',
  primary: '#EC4899',
  secondary: '#06B6D4',
  text: '#E2E8F0',
  textMuted: '#94A3B8',
};

const ITZAL = {
  bg: '#0d0820',
  gold: '#c9973a',
  cream: '#f2e8d0',
  red: '#8b1a1a',
  textMuted: '#b8a88a',
};

// Posicao da linha divisoria (em % da largura). Neutro = 50; hover expande a metade
// ativa ate 66/34. Animar essas variaveis e o que faz a pagina "se mexer".
const SPLIT = 50;
const GROW = 66;
const SHRINK = 34;

const MOBILE = '820px';

// Curva e duracao unicas pra tudo que se move junto com a linha
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const MOVE = `0.7s ${EASE}`;

// ─── Animacoes (keyframes) ────────────────────────────────────────────────────

const scanline = keyframes`
  0%   { transform: translateY(-30%); }
  100% { transform: translateY(130%); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 6px rgba(0,0,0,0.5), 0 0 18px rgba(201,151,58,0.4), 0 10px 40px rgba(0,0,0,0.7); }
  50%      { box-shadow: 0 0 0 6px rgba(0,0,0,0.5), 0 0 30px rgba(201,151,58,0.85), 0 10px 40px rgba(0,0,0,0.7); }
`;

const lightRay = keyframes`
  0%   { left: -160%; opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { left: 160%; opacity: 0; }
`;

const neonFlicker = keyframes`
  0%, 100%   { opacity: 1; text-shadow: 0 0 8px rgba(236,72,153,0.7), 0 0 22px rgba(236,72,153,0.45); }
  47%        { opacity: 1; }
  48%        { opacity: 0.6; text-shadow: none; }
  49%        { opacity: 1; text-shadow: 0 0 8px rgba(236,72,153,0.7), 0 0 22px rgba(236,72,153,0.45); }
  62%        { opacity: 1; }
  63%        { opacity: 0.72; text-shadow: 0 0 4px rgba(6,182,212,0.6); }
  64%        { opacity: 1; text-shadow: 0 0 8px rgba(236,72,153,0.7), 0 0 22px rgba(236,72,153,0.45); }
`;

const dividerShimmer = keyframes`
  0%, 100% { opacity: 0.7; }
  50%      { opacity: 1; }
`;

// Aberracao cromatica da logo CAP: 3 camadas com clip-path
const glitchColor = keyframes`
  0%, 85%, 100% { clip-path: inset(0 0 0 0); transform: translate(0); }
  86% { clip-path: inset(20% 0 60% 0); transform: translate(-4px, 0); }
  88% { clip-path: inset(50% 0 30% 0); transform: translate(4px, 0); }
  90% { clip-path: inset(10% 0 70% 0); transform: translate(-2px, 0); }
  92% { clip-path: inset(70% 0 10% 0); transform: translate(3px, 0); }
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.85; transform: scale(1); }
  50%      { opacity: 1; transform: scale(1.03); }
`;

// ─── Palco ────────────────────────────────────────────────────────────────────

const Stage = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: ${ITZAL.bg};

  @media (max-width: ${MOBILE}) {
    position: relative;
    min-height: 100vh;
    overflow: visible;
    display: flex;
    flex-direction: column;
  }
`;

// Cada metade e um bloco unico (fundo + conteudo). No desktop e camada absoluta
// clipada verticalmente na linha (a cor respeita a divisa). No mobile vira bloco
// relativo empilhado, sem clip.
const Side = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  transition: clip-path ${MOVE};
  will-change: clip-path;
  z-index: 1;

  /* Fundo (gradientes + cor base) */
  ${({ $side }) =>
    $side === 'left'
      ? css`
          background:
            radial-gradient(ellipse 60% 70% at 22% 32%, rgba(236,72,153,0.16) 0%, transparent 60%),
            radial-gradient(ellipse 50% 60% at 40% 85%, rgba(6,182,212,0.12) 0%, transparent 60%),
            ${CAP.bg};
        `
      : css`
          background:
            radial-gradient(ellipse 60% 70% at 78% 30%, rgba(201,151,58,0.18) 0%, transparent 60%),
            radial-gradient(ellipse 50% 60% at 62% 88%, rgba(139,26,26,0.16) 0%, transparent 60%),
            ${ITZAL.bg};
        `}

  @media (max-width: ${MOBILE}) {
    position: relative;
    inset: auto;
    clip-path: none !important;
    width: 100%;
    min-height: 46vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: auto;
    /* No mobile o brilho e ancorado na borda interna (junto da linha), pra a
       iluminacao alcancar a divisa em vez de sumir no meio do bloco. */
    ${({ $side }) =>
      $side === 'left'
        ? css`
            /* folga extra embaixo pra o medalhao que fica sobre a divisa respirar */
            padding: 3rem 1.5rem 5.5rem;
            background:
              radial-gradient(ellipse 95% 55% at 50% 100%, rgba(236,72,153,0.22) 0%, transparent 72%),
              radial-gradient(ellipse 80% 45% at 28% 8%, rgba(6,182,212,0.14) 0%, transparent 70%),
              ${CAP.bg};
          `
        : css`
            /* folga extra em cima pelo mesmo motivo */
            padding: 5.5rem 1.5rem 3rem;
            background:
              radial-gradient(ellipse 95% 55% at 50% 0%, rgba(201,151,58,0.26) 0%, transparent 72%),
              radial-gradient(ellipse 80% 45% at 72% 92%, rgba(139,26,26,0.18) 0%, transparent 70%),
              ${ITZAL.bg};
          `}
  }
`;

// Textura por cima do fundo: grid do CAP (CSS) ou escamas reais do ITZAL (webp).
const Texture = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;

  ${({ $side }) =>
    $side === 'left'
      ? css`
          background:
            linear-gradient(rgba(236,72,153,0.09) 1px, transparent 1px),
            linear-gradient(90deg, rgba(236,72,153,0.09) 1px, transparent 1px);
          background-size: 46px 46px;
          mask-image: radial-gradient(ellipse 90% 80% at 40% 45%, #000 40%, transparent 100%);

          &::after {
            content: '';
            position: absolute;
            left: 0; right: 0; top: 0;
            height: 45vh;
            background: linear-gradient(rgba(6,182,212,0), rgba(6,182,212,0.07), rgba(6,182,212,0));
            animation: ${scanline} 7s linear infinite;
          }
        `
      : css`
          background-image: url(${itzalScales});
          background-repeat: repeat;
          background-size: 240px;
          /* Escamas em relevo dourado: 'invert' joga o campo creme pro escuro e as
             linhas das escamas pro claro; a tintura leva pro dourado e o 'screen'
             deixa so as linhas brilharem sobre o fundo escuro do ITZAL. */
          filter: invert(1) sepia(1) saturate(3.2) hue-rotate(3deg) brightness(1.15) contrast(1.25);
          mix-blend-mode: screen;
          opacity: 0.62;
          mask-image: radial-gradient(ellipse 115% 100% at 60% 45%, #000 60%, transparent 100%);

          @media (max-width: ${MOBILE}) {
            background-size: 190px;
            opacity: 0.7;
            mask-image: radial-gradient(ellipse 125% 115% at 50% 42%, #000 70%, transparent 100%);
          }
        `}
`;

// Coluna de conteudo dentro da metade. No desktop e posicionada na faixa da metade
// (largura = pos da linha), centralizada, com padding que evita colar na linha/hub.
const Content = styled(motion.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1.3rem;
  transition: width ${MOVE}, opacity 0.5s ease;
  will-change: width, opacity;
  z-index: 3;

  ${({ $side }) =>
    $side === 'left'
      ? css`
          left: 0;
          padding: 3rem clamp(3.5rem, 8vw, 8rem) 3rem clamp(1.5rem, 4vw, 4rem);
        `
      : css`
          right: 0;
          padding: 3rem clamp(1.5rem, 4vw, 4rem) 3rem clamp(3.5rem, 8vw, 8rem);
        `}

  & > * { max-width: 360px; width: 100%; }

  ${({ $dimmed }) =>
    $dimmed &&
    css`
      opacity: 0.3;
      pointer-events: none;
    `}

  @media (max-width: ${MOBILE}) {
    position: relative;
    inset: auto;
    width: 100% !important;
    padding: 0 !important;
    opacity: 1 !important;
    pointer-events: auto !important;
  }
`;

// ─── Tipografia ───────────────────────────────────────────────────────────────

const Kicker = styled.p`
  font-size: 0.7rem;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  margin: 0 0 0.9rem;
  ${({ $itzal }) =>
    $itzal
      ? css`color: ${ITZAL.gold}; font-family: 'Cinzel', serif; letter-spacing: 0.28em;`
      : css`color: ${CAP.secondary}; font-family: 'Rajdhani', sans-serif; font-weight: 600;`}
`;

const CapTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-weight: 900;
  font-size: clamp(1.6rem, 3.4vw, 2.8rem);
  line-height: 1.05;
  text-transform: uppercase;
  margin: 0;
  color: ${CAP.text};
  animation: ${neonFlicker} 6s infinite;
`;

const ItzalTitle = styled.h1`
  font-family: 'Cinzel Decorative', 'Cinzel', serif;
  font-weight: 700;
  font-size: clamp(2.6rem, 6vw, 4.8rem);
  line-height: 0.95;
  letter-spacing: 0.05em;
  margin: 0;
  color: ${ITZAL.gold};
  text-shadow: 0 2px 30px rgba(201,151,58,0.35);
`;

const Sub = styled.p`
  margin: 0 auto;
  font-size: 0.94rem;
  line-height: 1.55;
  max-width: 320px;
  ${({ $itzal }) =>
    $itzal
      ? css`color: ${ITZAL.textMuted}; font-family: 'Lora', serif; font-style: italic;`
      : css`color: ${CAP.textMuted}; font-family: 'Rajdhani', sans-serif; font-weight: 500;`}
`;

// ─── Botoes ───────────────────────────────────────────────────────────────────

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-width: 300px;
  margin: 1.4rem auto 0;
`;

const btnBase = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 0.82rem 1.2rem;
  text-decoration: none;
  overflow: hidden;
  white-space: nowrap;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: -120%;
    width: 58%; height: 100%;
    background: linear-gradient(105deg, transparent 25%, rgba(255,255,255,0.35) 50%, transparent 75%);
    transition: left 0.5s ease;
  }
  &:hover::before { left: 130%; }
`;

const CapButton = styled(motion.a)`
  ${btnBase};
  font-family: 'Rajdhani', sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border-radius: 6px;
  color: ${({ $primary }) => ($primary ? CAP.bg : CAP.primary)};
  background: ${({ $primary }) => ($primary ? CAP.primary : 'rgba(236,72,153,0.06)')};
  border: 1.5px solid ${CAP.primary};
  box-shadow: ${({ $primary }) => ($primary ? '0 0 14px rgba(236,72,153,0.5)' : 'none')};
  transition: box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease;
  svg { font-size: 1.1rem; }
  &:hover {
    color: ${CAP.bg};
    background: ${CAP.primary};
    box-shadow: 0 0 18px rgba(236,72,153,0.7), 0 0 40px rgba(236,72,153,0.3);
  }
`;

const ItzalButton = styled(motion.a)`
  ${btnBase};
  font-family: 'Cinzel', serif;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border-radius: 3px;
  color: ${({ $primary }) => ($primary ? ITZAL.bg : ITZAL.gold)};
  background: ${({ $primary }) => ($primary ? ITZAL.gold : 'rgba(201,151,58,0.06)')};
  border: 1px solid rgba(201,151,58,0.55);
  box-shadow: ${({ $primary }) => ($primary ? '0 4px 22px rgba(201,151,58,0.45)' : 'none')};
  transition: filter 0.25s ease, background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
  svg { font-size: 1.05rem; }
  &:hover {
    filter: brightness(1.08);
    color: ${ITZAL.bg};
    background: ${ITZAL.gold};
    box-shadow: 0 4px 26px rgba(201,151,58,0.55);
  }
`;

// ─── Linha divisoria + hub central (desktop) ──────────────────────────────────

const Divider = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  transform: translateX(-50%);
  transition: left ${MOVE};
  background: linear-gradient(to bottom, transparent, ${CAP.primary} 22%, ${ITZAL.gold} 78%, transparent);
  box-shadow: 0 0 16px rgba(236,72,153,0.45), 0 0 16px rgba(201,151,58,0.4);
  animation: ${dividerShimmer} 3.5s ease-in-out infinite;
  pointer-events: none;
  z-index: 6;

  @media (max-width: ${MOBILE}) { display: none; }
`;

const CenterHub = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: left ${MOVE};
  z-index: 12;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  pointer-events: none;

  @media (max-width: ${MOBILE}) { display: none; }
`;

// Hub do mobile: sem banda propria. Fica montado EM CIMA da divisa entre as duas
// metades (metade em cada cenario) via margens negativas, com a logo no centro da
// linha e os botoes de rede um de cada lado dela.
const MobileHub = styled.div`
  display: none;

  @media (max-width: ${MOBILE}) {
    position: relative;
    z-index: 12;
    width: 100%;
    height: 100px;
    /* -50px = metade da altura: centraliza o hub exatamente sobre a divisa */
    margin: -50px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Fileira horizontal: [instagram] [logo] [email], centralizada na linha.
// z-index acima da MobileLine pra logo e botoes ficarem NA FRENTE da linha.
const MobileHubRow = styled.div`
  position: relative;
  z-index: 12;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1.4rem;
`;

const MobileLine = styled.div`
  display: none;
  @media (max-width: ${MOBILE}) {
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 2px;
    transform: translateY(-50%);
    background: linear-gradient(to right, transparent, ${CAP.primary} 22%, ${ITZAL.gold} 78%, transparent);
    box-shadow: 0 0 16px rgba(236,72,153,0.5), 0 0 20px rgba(201,151,58,0.42);
    animation: ${dividerShimmer} 3.5s ease-in-out infinite;
    z-index: 11;
  }
`;

const LogoRing = styled(motion.div)`
  position: relative;
  width: 118px;
  height: 118px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(201,151,58,0.65);
  background: radial-gradient(circle at 50% 40%, #1a1330, #0a0715);
  animation: ${glowPulse} 3.2s ease-in-out infinite;
  pointer-events: auto;
  cursor: default;

  @media (max-width: ${MOBILE}) { width: 100px; height: 100px; }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -160%;
    width: 55%;
    height: 200%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35) 40%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.35) 60%, transparent);
    transform: skewX(-20deg);
    animation: ${lightRay} 3.2s ease-in-out infinite;
    z-index: 2;
    pointer-events: none;
  }

  img {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 0.65rem;
  pointer-events: auto;
`;

const SocialBtn = styled(motion.a)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  color: ${ITZAL.cream};
  background: rgba(13,8,32,0.92);
  border: 1px solid rgba(201,151,58,0.55);
  box-shadow: 0 4px 16px rgba(0,0,0,0.6);
  text-decoration: none;
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: ${ITZAL.bg};
    background: ${ITZAL.gold};
    border-color: ${ITZAL.gold};
  }
`;

// ─── Ornamentos de canto ──────────────────────────────────────────────────────

function CornerOrnament({ style, className, ...rest }) {
  return (
    <svg
      viewBox="0 0 60 60"
      className={className}
      style={style}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M2 2 L20 2 M2 2 L2 20" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 2 L10 10" stroke="currentColor" strokeWidth="0.8" opacity="0.6" />
      <circle cx="2" cy="2" r="1.5" fill="currentColor" />
      <path d="M14 2 L14 8 M8 2 L8 5" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      <path d="M2 14 L8 14 M2 8 L5 8" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

const Ornament = styled(CornerOrnament)`
  position: absolute;
  width: 40px;
  height: 40px;
  color: ${ITZAL.gold};
  opacity: 0.5;
  z-index: 2;
  pointer-events: none;

  /* No mobile cada metade ocupa a largura toda: mostrar os 4 cantos, nao so 2 */
  &[data-corner-extra] { display: none; }
  @media (max-width: ${MOBILE}) {
    &[data-corner-extra] { display: block; }
  }
`;

const CapCorner = styled.span`
  position: absolute;
  width: 32px;
  height: 32px;
  border: 2px solid ${CAP.secondary};
  opacity: 0.45;
  z-index: 2;
  pointer-events: none;

  /* No mobile cada metade ocupa a largura toda: mostrar os 4 cantos, nao so 2 */
  &[data-corner-extra] { display: none; }
  @media (max-width: ${MOBILE}) {
    &[data-corner-extra] { display: block; }
  }
`;

// Marca d'agua: logo do CAP em branco com aberracao cromatica (cyan + vermelho),
// atras do titulo. Portado do efeito de glitch do rolador de dados (DicePool.tsx).
const ChromaticLogoWrap = styled.div`
  position: absolute;
  top: 50%;
  /* Desktop: centro da METADE do CAP (left vem inline = pos/2), acompanhando a linha */
  transform: translate(-50%, -50%);
  transition: left ${MOVE};
  width: min(60%, 420px);
  aspect-ratio: 1;
  z-index: 0;
  opacity: 0.16;
  pointer-events: none;

  @media (max-width: ${MOBILE}) {
    /* Mobile: o bloco do CAP ocupa a largura toda, centro e 50% mesmo */
    left: 50% !important;
    width: min(80%, 420px);
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const GhostLayer = styled.img`
  animation: ${breathe} 3s ease-in-out infinite;
`;

const CyanLayer = styled.img`
  filter: brightness(0) saturate(100%) invert(70%) sepia(98%) saturate(1000%) hue-rotate(150deg)
    brightness(103%) contrast(103%);
  mix-blend-mode: screen;
  animation: ${glitchColor} 3.2s infinite;
`;

const RedLayer = styled.img`
  filter: brightness(0) saturate(100%) invert(30%) sepia(100%) saturate(2000%) hue-rotate(330deg)
    brightness(90%) contrast(110%);
  mix-blend-mode: screen;
  animation: ${glitchColor} 3.2s infinite 0.05s;
`;

function ChromaticLogo({ centerX }) {
  return (
    <ChromaticLogoWrap style={{ left: `${centerX}%` }}>
      <GhostLayer src={capLogo} alt="" aria-hidden="true" />
      <CyanLayer src={capLogo} alt="" aria-hidden="true" />
      <RedLayer src={capLogo} alt="" aria-hidden="true" />
    </ChromaticLogoWrap>
  );
}

// ─── Hub reutilizavel (logo + redes) ──────────────────────────────────────────

function HubInner() {
  return (
    <>
      <LogoRing
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.35 }}
        whileHover={{ scale: 1.06 }}
      >
        <img src={logo} alt="Guilda Armorial" />
      </LogoRing>
      <SocialRow>
        <SocialBtn
          href="https://www.instagram.com/guildaarmorial/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
          whileHover={{ y: -3, scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaInstagram />
        </SocialBtn>
        <SocialBtn
          href="mailto:guildaarmorial@gmail.com"
          aria-label="Email"
          whileHover={{ y: -3, scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaEnvelope />
        </SocialBtn>
      </SocialRow>
    </>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SplitHome() {
  // null = neutro (50/50). 'left' = CAP em foco. 'right' = ITZAL em foco.
  const [focus, setFocus] = useState(null);

  // Posicao da linha em %: CAP em foco -> 66; ITZAL em foco -> 34.
  const pos = focus === 'left' ? GROW : focus === 'right' ? SHRINK : SPLIT;

  // Zonas de foco com faixa neutra larga no centro (38%-62%): perto do hub a
  // pagina fica parada (50/50), entao logo e botoes de rede nao fogem do clique.
  // A expansao so dispara com o cursor bem dentro de um dos lados.
  const handleMouseMove = (e) => {
    if (window.innerWidth <= 820) return; // mobile empilhado: sem expansao
    const x = e.clientX / window.innerWidth;
    const next = x < 0.38 ? 'left' : x > 0.62 ? 'right' : null;
    setFocus((prev) => (prev === next ? prev : next));
  };

  return (
    <Stage onMouseMove={handleMouseMove} onMouseLeave={() => setFocus(null)}>
      {/* ── Metade CAP (esquerda) ── */}
      <Side
        $side="left"
        style={{ clipPath: `polygon(0 0, ${pos}% 0, ${pos}% 100%, 0 100%)` }}
      >
        <Texture $side="left" />
        <ChromaticLogo centerX={pos / 2} />
        <CapCorner style={{ top: 20, left: 20, borderRight: 'none', borderBottom: 'none' }} />
        <CapCorner style={{ bottom: 20, left: 20, borderRight: 'none', borderTop: 'none' }} />
        <CapCorner
          data-corner-extra
          style={{ top: 20, right: 20, borderLeft: 'none', borderBottom: 'none' }}
        />
        <CapCorner
          data-corner-extra
          style={{ bottom: 20, right: 20, borderLeft: 'none', borderTop: 'none' }}
        />

        <Content
          $side="left"
          $dimmed={focus === 'right'}
          style={{ width: `${pos}%` }}
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <div>
            <Kicker>Iniciativa Nova Ordem</Kicker>
            <CapTitle>
              Condenados
              <br />
              ao Poder
            </CapTitle>
          </div>
          <Sub>
            RPG DE SUPER-HUMANOS EM UM DISTÓPICO MUNDO FUTURISTA
          </Sub>
          <ButtonRow>
            {/* BLOQUEADO enquanto o site do C.A.P. está em testes: sem href, não clica nem mostra o endereço.
                Para liberar, volte para:
                <CapButton href="/condenadosaopoder/" $primary whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <GiPerspectiveDiceSixFacesRandom /> Conheça o jogo
                </CapButton> */}
            <CapButton
              as="span"
              $primary
              aria-disabled="true"
              title="Em breve"
              style={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              <GiPerspectiveDiceSixFacesRandom /> Conheça o jogo · em breve
            </CapButton>
            <CapButton
              href="https://newordereditora.com/categoria-produto/rpg/condenados-ao-poder"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <GiBookCover /> Adquirir o Livro
            </CapButton>
          </ButtonRow>
        </Content>
      </Side>

      {/* ── Hub do MOBILE: montado sobre a divisa, logo no meio da linha e as
          redes uma de cada lado ── */}
      <MobileHub>
        <MobileLine />
        <MobileHubRow>
          <SocialBtn
            href="https://www.instagram.com/guildaarmorial/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            whileHover={{ y: -3, scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaInstagram />
          </SocialBtn>
          <LogoRing
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.35 }}
            whileHover={{ scale: 1.06 }}
          >
            <img src={logo} alt="Guilda Armorial" />
          </LogoRing>
          <SocialBtn
            href="mailto:guildaarmorial@gmail.com"
            aria-label="Email"
            whileHover={{ y: -3, scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaEnvelope />
          </SocialBtn>
        </MobileHubRow>
      </MobileHub>

      {/* ── Metade ITZAL (direita) ── */}
      <Side
        $side="right"
        style={{ clipPath: `polygon(${pos}% 0, 100% 0, 100% 100%, ${pos}% 100%)` }}
      >
        <Texture $side="right" />
        <Ornament style={{ top: 20, right: 20, transform: 'rotate(90deg)' }} />
        <Ornament style={{ bottom: 20, right: 20, transform: 'rotate(180deg)' }} />
        <Ornament data-corner-extra style={{ top: 20, left: 20, transform: 'rotate(0deg)' }} />
        <Ornament data-corner-extra style={{ bottom: 20, left: 20, transform: 'rotate(-90deg)' }} />

        <Content
          $side="right"
          $dimmed={focus === 'left'}
          style={{ width: `${100 - pos}%` }}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <div>
            <Kicker $itzal>Fantasia Medieval Recifense</Kicker>
            <ItzalTitle>ITZAL</ItzalTitle>
          </div>
          <Sub $itzal>
            Molde seu destino numa fantasia medieval com a alma do Recife. Uma mistura de RPG com
            jogo de cartas.
          </Sub>
          <ButtonRow>
            <ItzalButton
              href="https://guildaarmorial.com/itzal/"
              target="_blank"
              rel="noopener noreferrer"
              $primary
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <GiDragonHead /> Landpage
            </ItzalButton>
            <ItzalButton
              href="https://forms.gle/84rRwcAzXTn5eQK68"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <GiScrollQuill /> Feedbacks
            </ItzalButton>
          </ButtonRow>
        </Content>
      </Side>

      {/* ── Linha + hub central do DESKTOP ── */}
      <Divider style={{ left: `${pos}%` }} />
      <CenterHub style={{ left: `${pos}%` }}>
        <HubInner />
      </CenterHub>
    </Stage>
  );
}
